import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import {
  Button,
  Space,
  message,
  Popconfirm,
  Tooltip,
  Tag,
  Modal,
  Input,
  Checkbox as AntCheckbox,
} from 'antd';
import {
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  SearchOutlined,
  ColumnHeightOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { businessService, Business } from '../services/businesses';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { useListingTableFocus } from '../hooks/useListingTableFocus';
import { useListingColumnsPersistence } from '../hooks/useListingColumnsPersistence';
import { useListingColumnWidthsPersistence } from '../hooks/useListingColumnWidthsPersistence';
import { useListingSortPersistence } from '../hooks/useListingSortPersistence';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import '../styles/resizable-table.css';
import {
  Table as FlowbiteTable,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Alert,
} from 'flowbite-react';
import { Resizable } from 'react-resizable';

type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};
type FilterType = 'all' | 'potential' | 'active' | 'inactive';
type SortOrder = 'ascend' | 'descend' | null;

const formatDateValue = (date?: string) => (date ? dayjs(date).format('DD/MM/YYYY') : '');

interface FlowbiteResizableHeaderProps {
  width?: number | string;
  maxWidth?: number;
  onResize?: (e: any, data: { size: { width: number } }) => void;
  onClick?: () => void;
  className?: string;
  children: React.ReactNode;
}

const FlowbiteResizableHeader: React.FC<FlowbiteResizableHeaderProps> = ({
  width,
  maxWidth,
  onResize,
  onClick,
  className,
  children,
}) => {
  if (typeof width !== 'number' || !onResize) {
    return (
      <TableHeadCell
        className={className}
        style={width ? { width } : undefined}
        onClick={onClick}
      >
        {children}
      </TableHeadCell>
    );
  }

  return (
    <Resizable
      width={width}
      height={0}
      onResize={(e, data) => {
        const newWidth = Math.max(data.size.width, 50);
        onResize(e, { ...data, size: { ...data.size, width: newWidth } });
      }}
      resizeHandles={['e']}
      handle={
        <span
          className="resizable-table-handle"
          style={{ height: '100%' }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
      }
      draggableOpts={{ enableUserSelectHack: false }}
      maxConstraints={typeof maxWidth === 'number' ? [maxWidth, 0] : undefined}
      minConstraints={[50, 0]}
    >
      <TableHeadCell
        className={className ? `relative ${className}` : 'relative'}
        style={{ width }}
        onClick={onClick}
      >
        {children}
      </TableHeadCell>
    </Resizable>
  );
};

export const Businesses: React.FC = () => {
  const listingKey = 'business';
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [submitNotice, setSubmitNotice] = useState<{ type: 'success' | 'failure'; message: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [excludedRowKeys, setExcludedRowKeys] = useState<React.Key[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);
  const [columnWidthByKey, setColumnWidthByKey] = useState<Record<string, number>>({});
  const [columnsModalOpen, setColumnsModalOpen] = useState(false);
  const [columnsSearch, setColumnsSearch] = useState('');
  const [pendingVisibleKeys, setPendingVisibleKeys] = useState<React.Key[]>([]);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const [availableWidth, setAvailableWidth] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [statusCountsFromApi, setStatusCountsFromApi] = useState<{ all: number; potential: number; active: number; inactive: number } | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { setHeaderContent } = useOutletContext<LayoutHeaderContext>();

  useEffect(() => {
    const stateNotice = (location.state as { notice?: { type: 'success' | 'failure'; message: string } } | null)?.notice;
    if (stateNotice) {
      setSubmitNotice(stateNotice);
      navigate(location.pathname + location.search, { replace: true });
    }
  }, [location, navigate]);
  const { user } = useAuth();
  const canView = hasPermission(user, 'customer.business.view');
  const canCreate = hasPermission(user, 'customer.business.create');
  const canDelete = hasPermission(user, 'customer.business.delete');
  const statusCounts = useMemo(() => {
    if (statusCountsFromApi) {
      return statusCountsFromApi;
    }
    const counts = { all: businesses.length, potential: 0, active: 0, inactive: 0 };
    businesses.forEach((business) => {
      if (business.type === 'potential') counts.potential += 1;
      if (business.type === 'active') counts.active += 1;
      if (business.type === 'inactive') counts.inactive += 1;
    });
    return counts;
  }, [businesses, statusCountsFromApi]);


  useEffect(() => {
    if (!canView) {
      setBusinesses([]);
      return;
    }
    loadBusinesses();
  }, [canView]);


  useEffect(() => {
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Businesses
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const loadBusinesses = async () => {
    if (!canView) {
      return;
    }
    setLoading(true);
    try {
      const response = await businessService.getAll();
      const { items, total, statusCounts: apiStatusCounts } = normalizeBusinessesResponse(response);
      setBusinesses(items);
      const resolvedTotal = typeof total === 'number' && total > 0 ? total : items.length;
      setTotalCount(resolvedTotal);
      setStatusCountsFromApi(apiStatusCounts);
    } catch {
      message.error('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const normalizeBusinessesResponse = (response: any) => {
    const raw = response?.data ?? response;
    const meta = raw?.meta;
    const payload = raw?.data && !Array.isArray(raw?.data) ? raw.data : raw;
    const items = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : [];
    const total = Number(payload?.total ?? meta?.total ?? 0);
    const nextPageExists = false;
    const statusCountsMeta = meta?.status_counts;
    const statusCounts = statusCountsMeta
      ? {
          all: total || 0,
          potential: Number(statusCountsMeta.potential ?? 0),
          active: Number(statusCountsMeta.active ?? 0),
          inactive: Number(statusCountsMeta.inactive ?? 0),
        }
      : null;
    return { items, nextPageExists, total, statusCounts };
  };

  const handleBatchDelete = async () => {
    if (!selectAll && selectedRowKeys.length === 0) {
      return;
    }
    setDeleting(true);
    try {
      const filters = filter === 'all' ? {} : { type: filter };
      const payload = selectAll
        ? { select_all: true, excluded_ids: excludedRowKeys.map((key) => Number(key)), filters }
        : { ids: selectedRowKeys.map((key) => Number(key)), filters };
      await businessService.batchDelete(payload);
      message.success('Businesses deleted successfully');
      setSelectedRowKeys([]);
      setSelectAll(false);
      setExcludedRowKeys([]);
      loadBusinesses();
    } catch {
      message.error('Failed to delete businesses');
    } finally {
      setDeleting(false);
    }
  };

  const enabledBusinesses = useMemo(
    () => businesses.filter((business) => (business as any).enabled !== -1),
    [businesses]
  );

  const handleImportNavigate = () => {
    navigate('/businesses/import');
  };

  const handleSort = (key: string, sorter?: unknown) => {
    if (typeof sorter !== 'function') {
      return;
    }
    if (sortKey !== key) {
      setSortKey(key);
      setSortOrder('ascend');
      persistSort(key, 'ascend');
      return;
    }
    if (sortOrder === 'ascend') {
      setSortOrder('descend');
      persistSort(key, 'descend');
      return;
    }
    setSortKey(null);
    setSortOrder(null);
    persistSort(null, null);
  };

  const getCellValue = (record: Business, col: any) => {
    if (!col.dataIndex) {
      return undefined;
    }
    if (Array.isArray(col.dataIndex)) {
      return col.dataIndex.reduce((acc: any, key: string) => (acc == null ? undefined : acc[key]), record);
    }
    return (record as any)[col.dataIndex];
  };

  const baseColumns = useMemo(
    () => [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      sorter: (a: Business, b: Business) => (a.name || '').localeCompare(b.name || ''),
      render: (val: string | undefined) => {
        const displayValue = val || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Former Name',
      dataIndex: 'former_name',
      key: 'former_name',
      width: 180,
      sorter: (a: Business, b: Business) => (a.former_name || '').localeCompare(b.former_name || ''),
      render: (val: string | undefined) => val || '-',
    },
    {
      title: 'Registration No.',
      dataIndex: 'registration_no',
      key: 'registration_no',
      width: 160,
      sorter: (a: Business, b: Business) => (a.registration_no || '').localeCompare(b.registration_no || ''),
    },
    {
      title: 'Entity',
      dataIndex: 'entity',
      key: 'entity',
      width: 140,
      sorter: (a: Business, b: Business) => (a.entity || '').localeCompare(b.entity || ''),
    },
    {
      title: 'Tin No.',
      dataIndex: 'tin_no',
      key: 'tin_no',
      width: 140,
      sorter: (a: Business, b: Business) => (a.tin_no || '').localeCompare(b.tin_no || ''),
    },
    {
      title: 'Incorporation',
      dataIndex: 'incorporation_date',
      key: 'incorporation_date',
      width: 140,
      sorter: (a: Business, b: Business) => (a.incorporation_date || '').localeCompare(b.incorporation_date || ''),
      render: (val: string | undefined) => (val ? dayjs(val).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Year End',
      dataIndex: 'year_end',
      key: 'year_end',
      width: 120,
      sorter: (a: Business, b: Business) => (a.year_end || '').localeCompare(b.year_end || ''),
      render: (val: string | undefined) => val || '-',
    },
    {
      title: 'Contact No.',
      dataIndex: 'contact_number',
      key: 'contact_number',
      width: 160,
      sorter: (a: Business, b: Business) =>
        (a.contacts?.[0]?.contact_number || '').localeCompare(b.contacts?.[0]?.contact_number || ''),
      render: (_: unknown, record: Business) => record.contacts?.[0]?.contact_number || '-',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      sorter: (a: Business, b: Business) => (a.contacts?.[0]?.email || '').localeCompare(b.contacts?.[0]?.email || ''),
      render: (_: unknown, record: Business) => record.contacts?.[0]?.email || '-',
    },
    {
      title: 'Address',
      dataIndex: 'address1',
      key: 'address1',
      width: 240,
      visible: false,
      sorter: (a: Business, b: Business) =>
        (a.contacts?.[0]?.main_address1 || '').localeCompare(b.contacts?.[0]?.main_address1 || ''),
      render: (_: unknown, record: Business) => record.contacts?.[0]?.main_address1 || '-',
    },
    {
      title: 'MSIC Codes',
      dataIndex: 'msic_codes',
      key: 'msic_codes',
      width: 200,
      visible: false,
      render: (_: unknown, record: Business) => {
        const nature = record.natures?.[0];
        const codes = [nature?.msic_code_1, nature?.msic_code_2, nature?.msic_code_3, nature?.msic_code_4, nature?.msic_code_5]
          .filter((code) => Boolean(code))
          .join(', ');
        return codes || '-';
      },
    },
    {
      title: 'Business Description',
      dataIndex: 'business_description',
      key: 'business_description',
      width: 220,
      visible: false,
      render: (_: unknown, record: Business) => record.natures?.[0]?.business_description || '-',
    },
    {
      title: 'Trade Mark',
      dataIndex: 'trade_mark',
      key: 'trade_mark',
      width: 160,
      visible: false,
      render: (_: unknown, record: Business) => (record.natures?.[0]?.trademark_logo ? 'Uploaded' : '-'),
    },
    {
      title: 'Industry License',
      dataIndex: 'industry_license',
      key: 'industry_license',
      width: 180,
      visible: false,
      render: (_: unknown, record: Business) => (record.natures?.[0]?.industry_specific_license ? 'Uploaded' : '-'),
    },
    {
      title: 'Bank',
      dataIndex: 'bank',
      key: 'bank',
      width: 160,
      visible: false,
      sorter: (a: Business, b: Business) =>
        (a.financials?.[0]?.bank || '').localeCompare(b.financials?.[0]?.bank || ''),
      render: (_: unknown, record: Business) => record.financials?.[0]?.bank || '-',
    },
    {
      title: 'KWSP No.',
      dataIndex: 'kwsp_id',
      key: 'kwsp_id',
      width: 140,
      visible: false,
      render: (_: unknown, record: Business) => record.employers?.[0]?.kwsp_id || '-',
    },
    {
      title: 'KWSP Login',
      dataIndex: 'kwsp_login',
      key: 'kwsp_login',
      width: 140,
      visible: false,
      render: (_: unknown, record: Business) => record.employers?.[0]?.kwsp_account_no || '-',
    },
    {
      title: 'KWSP Password',
      dataIndex: 'kwsp_password',
      key: 'kwsp_password',
      width: 160,
      visible: false,
      render: (_: unknown, record: Business) => record.employers?.[0]?.kwsp_password || '-',
    },
    {
      title: 'PERKESO No.',
      dataIndex: 'perkeso_id',
      key: 'perkeso_id',
      width: 150,
      visible: false,
      render: (_: unknown, record: Business) => record.employers?.[0]?.perkeso_id || '-',
    },
    {
      title: 'PERKESO Login',
      dataIndex: 'perkeso_login',
      key: 'perkeso_login',
      width: 160,
      visible: false,
      render: (_: unknown, record: Business) => record.employers?.[0]?.perkeso_account_no || '-',
    },
    {
      title: 'PERKESO Password',
      dataIndex: 'perkeso_password',
      key: 'perkeso_password',
      width: 170,
      visible: false,
      render: (_: unknown, record: Business) => record.employers?.[0]?.perkeso_password || '-',
    },
    {
      title: 'PCB No.',
      dataIndex: 'pcb',
      key: 'pcb',
      width: 120,
      visible: false,
      render: (_: unknown, record: Business) => record.employers?.[0]?.pcb || '-',
    },
    {
      title: 'PCB Login',
      dataIndex: 'pcb_login',
      key: 'pcb_login',
      width: 140,
      visible: false,
      render: (_: unknown, record: Business) => '-',
    },
    {
      title: 'PCB Password',
      dataIndex: 'pcb_password',
      key: 'pcb_password',
      width: 150,
      visible: false,
      render: (_: unknown, record: Business) => '-',
    },
    {
      title: 'HRDC No.',
      dataIndex: 'hrdc_id',
      key: 'hrdc_id',
      width: 140,
      visible: false,
      render: (_: unknown, record: Business) => record.employers?.[0]?.hrdc_id || '-',
    },
    {
      title: 'HRDC Login',
      dataIndex: 'hrdc_login',
      key: 'hrdc_login',
      width: 150,
      visible: false,
      render: (_: unknown, record: Business) => record.employers?.[0]?.hrdc_account_no || '-',
    },
    {
      title: 'HRDC Password',
      dataIndex: 'hrdc_password',
      key: 'hrdc_password',
      width: 160,
      visible: false,
      render: (_: unknown, record: Business) => record.employers?.[0]?.hrdc_password || '-',
    },
    {
      title: 'SST No.',
      dataIndex: 'sst_id',
      key: 'sst_id',
      width: 140,
      visible: false,
      render: (_: unknown, record: Business) => record.employers?.[0]?.sst_id || '-',
    },
    {
      title: 'SST Login',
      dataIndex: 'sst_login',
      key: 'sst_login',
      width: 140,
      visible: false,
      render: (_: unknown, record: Business) => record.employers?.[0]?.sst_account_no || '-',
    },
    {
      title: 'SST Password',
      dataIndex: 'sst_password',
      key: 'sst_password',
      width: 160,
      visible: false,
      render: (_: unknown, record: Business) => record.employers?.[0]?.sst_password || '-',
    },
    {
      title: 'Company Chop',
      dataIndex: 'company_chop',
      key: 'company_chop',
      width: 160,
      visible: false,
      render: (_: unknown, record: Business) => (record.supportings?.[0]?.company_chop ? 'Uploaded' : '-'),
    },
    {
      title: 'Advisor',
      dataIndex: 'advisor_staff_id',
      key: 'advisor_staff_id',
      width: 160,
      visible: false,
      render: (_: unknown, record: Business) => (record as any).advisor_staff?.name || (record as any).advisorStaff?.name || '-',
    },
    {
      title: 'Advisor Appoint',
      dataIndex: 'advisor_appoint_date',
      key: 'advisor_appoint_date',
      width: 160,
      visible: false,
      render: (_: unknown, record: Business) => ((record as any).advisor_appoint_date ? dayjs((record as any).advisor_appoint_date).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Advisor Resign',
      dataIndex: 'advisor_resign_date',
      key: 'advisor_resign_date',
      width: 160,
      visible: false,
      render: (_: unknown, record: Business) => ((record as any).advisor_resign_date ? dayjs((record as any).advisor_resign_date).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Accountant',
      dataIndex: 'account_staff_id',
      key: 'account_staff_id',
      width: 160,
      visible: false,
      render: (_: unknown, record: Business) => (record as any).account_staff?.name || (record as any).accountStaff?.name || '-',
    },
    {
      title: 'Accountant Appoint',
      dataIndex: 'account_appoint_date',
      key: 'account_appoint_date',
      width: 170,
      visible: false,
      render: (_: unknown, record: Business) => ((record as any).account_appoint_date ? dayjs((record as any).account_appoint_date).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Accountant Resign',
      dataIndex: 'account_resign_date',
      key: 'account_resign_date',
      width: 170,
      visible: false,
      render: (_: unknown, record: Business) => ((record as any).account_resign_date ? dayjs((record as any).account_resign_date).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Tax Agent',
      dataIndex: 'tax_staff_id',
      key: 'tax_staff_id',
      width: 160,
      visible: false,
      render: (_: unknown, record: Business) => (record as any).tax_staff?.name || (record as any).taxStaff?.name || '-',
    },
    {
      title: 'Tax Agent Appoint',
      dataIndex: 'tax_appoint_date',
      key: 'tax_appoint_date',
      width: 170,
      visible: false,
      render: (_: unknown, record: Business) => ((record as any).tax_appoint_date ? dayjs((record as any).tax_appoint_date).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Tax Agent Resign',
      dataIndex: 'tax_resign_date',
      key: 'tax_resign_date',
      width: 170,
      visible: false,
      render: (_: unknown, record: Business) => ((record as any).tax_resign_date ? dayjs((record as any).tax_resign_date).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Auditor',
      dataIndex: 'audit_staff_id',
      key: 'audit_staff_id',
      width: 160,
      visible: false,
      render: (_: unknown, record: Business) => (record as any).audit_staff?.name || (record as any).auditStaff?.name || '-',
    },
    {
      title: 'Auditor Appoint',
      dataIndex: 'audit_appoint_date',
      key: 'audit_appoint_date',
      width: 170,
      visible: false,
      render: (_: unknown, record: Business) => ((record as any).audit_appoint_date ? dayjs((record as any).audit_appoint_date).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Auditor Resign',
      dataIndex: 'audit_resign_date',
      key: 'audit_resign_date',
      width: 170,
      visible: false,
      render: (_: unknown, record: Business) => ((record as any).audit_resign_date ? dayjs((record as any).audit_resign_date).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Secretary',
      dataIndex: 'secretary_staff_id',
      key: 'secretary_staff_id',
      width: 160,
      visible: false,
      render: (_: unknown, record: Business) => (record as any).secretary_staff?.name || (record as any).secretaryStaff?.name || '-',
    },
    {
      title: 'Secretary Appoint',
      dataIndex: 'secretary_appoint_date',
      key: 'secretary_appoint_date',
      width: 170,
      visible: false,
      render: (_: unknown, record: Business) => ((record as any).secretary_appoint_date ? dayjs((record as any).secretary_appoint_date).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Secretary Resign',
      dataIndex: 'secretary_resign_date',
      key: 'secretary_resign_date',
      width: 170,
      visible: false,
      render: (_: unknown, record: Business) => ((record as any).secretary_resign_date ? dayjs((record as any).secretary_resign_date).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Directors',
      dataIndex: 'directors',
      key: 'directors',
      width: 120,
      visible: false,
      render: (_: unknown, record: Business) => record.directors?.length ?? 0,
    },
    {
      title: 'Members',
      dataIndex: 'members',
      key: 'members',
      width: 120,
      visible: false,
      render: (_: unknown, record: Business) => record.members?.length ?? 0,
    },
    {
      title: 'Request',
      dataIndex: 'pending_requests',
      key: 'pending_requests',
      width: 110,
      render: (_: unknown, record: Business) => {
        const pendingCount =
          (record.pending_business_requests_count || 0) +
          (record.pending_engagement_requests_count || 0) +
          (record.pending_nature_requests_count || 0) +
          (record.pending_employer_requests_count || 0) +
          (record.pending_change_requests_count || 0);
        const hasPending = pendingCount > 0;
        return <Tag color={hasPending ? 'green' : 'default'}>{hasPending ? 'Yes' : 'No'}</Tag>;
      },
    },
    ],
    []
  );

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
    setColumnConfig,
  } = useColumnVisibility(baseColumns);
  const maxVisibleColumns = 10;
  const { persistVisibleKeys } = useListingColumnsPersistence(
    listingKey,
    baseColumns,
    maxVisibleColumns,
    setColumnConfig
  );
  const { persistColumnWidths } = useListingColumnWidthsPersistence(
    listingKey,
    baseColumns,
    setColumnWidthByKey
  );
  const { persistSort } = useListingSortPersistence(
    listingKey,
    baseColumns,
    setSortKey,
    setSortOrder
  );
  const columnGroups = useMemo(
    () => [
      {
        title: 'Details',
        keys: [
          'name',
          'former_name',
          'registration_no',
          'entity',
          'tin_no',
          'incorporation_date',
          'year_end',
          'contact_number',
          'email',
          'pending_requests',
        ],
      },
      {
        title: 'Address',
        keys: ['address1'],
      },
      {
        title: 'MSIC Classification',
        keys: ['msic_codes'],
      },
      {
        title: 'Nature of Business',
        keys: ['business_description', 'trade_mark', 'industry_license'],
      },
      {
        title: 'Financial Information',
        keys: ['bank'],
      },
      {
        title: 'Account',
        keys: [
          'kwsp_id',
          'perkeso_id',
          'pcb',
          'hrdc_id',
          'sst_id',
          'kwsp_login',
          'perkeso_login',
          'pcb_login',
          'hrdc_login',
          'sst_login',
          'kwsp_password',
          'perkeso_password',
          'pcb_password',
          'hrdc_password',
          'sst_password',
        ],
      },
      {
        title: 'Company Chop',
        keys: ['company_chop'],
      },
      {
        title: 'Engagement',
        keys: [
          'advisor_staff_id',
          'account_staff_id',
          'tax_staff_id',
          'audit_staff_id',
          'secretary_staff_id',
          'advisor_appoint_date',
          'account_appoint_date',
          'tax_appoint_date',
          'audit_appoint_date',
          'secretary_appoint_date',
          'advisor_resign_date',
          'account_resign_date',
          'tax_resign_date',
          'audit_resign_date',
          'secretary_resign_date',
        ],
      },
      {
        title: 'Directors',
        keys: ['directors'],
      },
      {
        title: 'Members',
        keys: ['members'],
      },
    ],
    []
  );
  const defaultVisibleKeys = useMemo(
    () => baseColumns.filter((col) => !('visible' in col) || (col as { visible?: boolean }).visible !== false).map((col) => col.key),
    [baseColumns]
  );
  const handleColumnVisibilityChangeLimited = useCallback(
    (keys: React.Key[]) => {
      if (keys.length > maxVisibleColumns) {
        message.warning(`You can select up to ${maxVisibleColumns} columns.`);
        return;
      }
      handleColumnVisibilityChange(keys);
      persistVisibleKeys(keys);
    },
    [handleColumnVisibilityChange, maxVisibleColumns, persistVisibleKeys]
  );
  const columnSelectionLocked = pendingVisibleKeys.length >= maxVisibleColumns;
  const filteredColumnConfig = useMemo(() => {
    if (!columnsSearch.trim()) {
      return columnConfig;
    }
    const query = columnsSearch.toLowerCase();
    return columnConfig.filter((col) => col.title.toLowerCase().includes(query));
  }, [columnConfig, columnsSearch]);
  const columnConfigByKey = useMemo(
    () => new Map(columnConfig.map((col) => [col.key, col])),
    [columnConfig]
  );

  const getSearchValueForColumn = useCallback((col: any) => {
    if (col.dataIndex === 'name') return (record: Business) => record.name;
    if (col.dataIndex === 'registration_no') return (record: Business) => record.registration_no;
    if (col.dataIndex === 'tin_no') return (record: Business) => record.tin_no;
    if (col.dataIndex === 'entity') return (record: Business) => record.entity;
    if (col.dataIndex === 'engagement') return (record: Business) => record.engagement;
    if (col.dataIndex === 'agent') return (record: Business) => record.agent?.name;
    if (col.dataIndex === 'staff') return (record: Business) => record.staff?.name;
    if (col.dataIndex === 'constitution') return (record: Business) => (record.constitution ? 'Yes' : 'No');
    if (col.dataIndex === 'common_seal') return (record: Business) => (record.common_seal ? 'Yes' : 'No');
    if (col.dataIndex === 'former_name') return (record: Business) => record.former_name;
    if (col.dataIndex === 'address1') return (record: Business) => record.contacts?.[0]?.main_address1;
    if (col.dataIndex === 'msic_codes') return (record: Business) => {
      const nature = record.natures?.[0];
      return [nature?.msic_code_1, nature?.msic_code_2, nature?.msic_code_3, nature?.msic_code_4, nature?.msic_code_5]
        .filter((code) => Boolean(code))
        .join(', ');
    };
    if (col.dataIndex === 'business_description') return (record: Business) => record.natures?.[0]?.business_description;
    if (col.dataIndex === 'trade_mark') return (record: Business) => (record.natures?.[0]?.trademark_logo ? 'Uploaded' : '');
    if (col.dataIndex === 'industry_license') return (record: Business) => (record.natures?.[0]?.industry_specific_license ? 'Uploaded' : '');
    if (col.dataIndex === 'bank') return (record: Business) => record.financials?.[0]?.bank;
    if (col.dataIndex === 'kwsp_id') return (record: Business) => record.employers?.[0]?.kwsp_id;
    if (col.dataIndex === 'kwsp_login') return (record: Business) => record.employers?.[0]?.kwsp_account_no;
    if (col.dataIndex === 'kwsp_password') return (record: Business) => record.employers?.[0]?.kwsp_password;
    if (col.dataIndex === 'perkeso_id') return (record: Business) => record.employers?.[0]?.perkeso_id;
    if (col.dataIndex === 'perkeso_login') return (record: Business) => record.employers?.[0]?.perkeso_account_no;
    if (col.dataIndex === 'perkeso_password') return (record: Business) => record.employers?.[0]?.perkeso_password;
    if (col.dataIndex === 'pcb') return (record: Business) => record.employers?.[0]?.pcb;
    if (col.dataIndex === 'pcb_login') return () => '';
    if (col.dataIndex === 'pcb_password') return () => '';
    if (col.dataIndex === 'hrdc_id') return (record: Business) => record.employers?.[0]?.hrdc_id;
    if (col.dataIndex === 'hrdc_login') return (record: Business) => record.employers?.[0]?.hrdc_account_no;
    if (col.dataIndex === 'hrdc_password') return (record: Business) => record.employers?.[0]?.hrdc_password;
    if (col.dataIndex === 'sst_id') return (record: Business) => record.employers?.[0]?.sst_id;
    if (col.dataIndex === 'sst_login') return (record: Business) => record.employers?.[0]?.sst_account_no;
    if (col.dataIndex === 'sst_password') return (record: Business) => record.employers?.[0]?.sst_password;
    if (col.dataIndex === 'company_chop') return (record: Business) => (record.supportings?.[0]?.company_chop ? 'Uploaded' : '');
    if (col.dataIndex === 'pending_requests') return (record: Business) => {
      const pendingCount =
        (record.pending_business_requests_count || 0) +
        (record.pending_engagement_requests_count || 0) +
        (record.pending_nature_requests_count || 0) +
        (record.pending_employer_requests_count || 0) +
        (record.pending_change_requests_count || 0);
      return pendingCount > 0 ? 'Yes' : 'No';
    };
    if (col.dataIndex === 'advisor_staff_id') return (record: Business) => (record as any).advisor_staff?.name || (record as any).advisorStaff?.name;
    if (col.dataIndex === 'advisor_appoint_date') return (record: Business) => formatDateValue((record as any).advisor_appoint_date);
    if (col.dataIndex === 'advisor_resign_date') return (record: Business) => formatDateValue((record as any).advisor_resign_date);
    if (col.dataIndex === 'account_staff_id') return (record: Business) => (record as any).account_staff?.name || (record as any).accountStaff?.name;
    if (col.dataIndex === 'account_appoint_date') return (record: Business) => formatDateValue((record as any).account_appoint_date);
    if (col.dataIndex === 'account_resign_date') return (record: Business) => formatDateValue((record as any).account_resign_date);
    if (col.dataIndex === 'tax_staff_id') return (record: Business) => (record as any).tax_staff?.name || (record as any).taxStaff?.name;
    if (col.dataIndex === 'tax_appoint_date') return (record: Business) => formatDateValue((record as any).tax_appoint_date);
    if (col.dataIndex === 'tax_resign_date') return (record: Business) => formatDateValue((record as any).tax_resign_date);
    if (col.dataIndex === 'audit_staff_id') return (record: Business) => (record as any).audit_staff?.name || (record as any).auditStaff?.name;
    if (col.dataIndex === 'audit_appoint_date') return (record: Business) => formatDateValue((record as any).audit_appoint_date);
    if (col.dataIndex === 'audit_resign_date') return (record: Business) => formatDateValue((record as any).audit_resign_date);
    if (col.dataIndex === 'secretary_staff_id') return (record: Business) => (record as any).secretary_staff?.name || (record as any).secretaryStaff?.name;
    if (col.dataIndex === 'secretary_appoint_date') return (record: Business) => formatDateValue((record as any).secretary_appoint_date);
    if (col.dataIndex === 'secretary_resign_date') return (record: Business) => formatDateValue((record as any).secretary_resign_date);
    if (col.dataIndex === 'directors') return (record: Business) => `${record.directors?.length ?? 0}`;
    if (col.dataIndex === 'members') return (record: Business) => `${record.members?.length ?? 0}`;
    const key = String(col.key || '').toLowerCase();
    if (typeof col.dataIndex === 'string') {
      const dataIndex = col.dataIndex.toLowerCase();
      if (dataIndex.includes('date') || dataIndex.includes('due') || dataIndex.includes('appoint') || dataIndex.includes('resign')) {
        return (record: Business) => formatDateValue((record as any)[col.dataIndex]);
      }
    }
    if (key.includes('date') || key.includes('due') || key.includes('appoint') || key.includes('resign')) {
      return (record: Business) => formatDateValue((record as any)[col.key]);
    }
    return undefined as unknown as (record: Business) => string | number | null | undefined;
  }, []);

  const searchColumns = useMemo(
    () =>
      visibleColumns.map((col) => ({
        ...col,
        getSearchValue: getSearchValueForColumn(col),
      })),
    [visibleColumns, getSearchValueForColumn]
  );

  const memoizedColumns = useMemo(
    () =>
      visibleColumns.map((col) => ({
        ...col,
        width: col.width,
        getSearchValue: getSearchValueForColumn(col),
      })),
    [visibleColumns, getSearchValueForColumn]
  );

  const typeFilteredBusinesses = useMemo(() => {
    if (filter === 'potential') return enabledBusinesses.filter(b => b.type === 'potential');
    if (filter === 'active') return enabledBusinesses.filter(b => b.type === 'active');
    if (filter === 'inactive') return enabledBusinesses.filter(b => b.type === 'inactive');
    return enabledBusinesses;
  }, [enabledBusinesses, filter]);

  const { columnsWithSearch, filteredData, searchRowCells } = useTableColumnSearch<Business>(
    typeFilteredBusinesses,
    memoizedColumns as any,
    searchColumns as any,
    { splitHeader: true, listingKey }
  );

  const tableColumns = columnsWithSearch as any[];
  const totalValue = filteredData.length;
  const totalLabel = `Total: ${totalValue} ${totalValue === 1 ? 'business' : 'businesses'}`;
  const totalColumnCount = tableColumns.length + 1;
  const minColumnWidth = 50;
  const baseColumnWidths = useMemo(
    () =>
      tableColumns.map((col) => {
        const key = String(col.key);
        const cached = columnWidthByKey[key];
        if (typeof cached === 'number') {
          return cached;
        }
        if (typeof col.width === 'number') {
          return col.width;
        }
        if (typeof col.width === 'string') {
          const parsed = Number.parseFloat(col.width);
          if (Number.isFinite(parsed)) {
            return parsed;
          }
        }
        return 150;
      }),
    [tableColumns, columnWidthByKey]
  );
  const columnWidths = useMemo(() => {
    if (availableWidth == null) {
      return baseColumnWidths;
    }
    const total = baseColumnWidths.reduce((sum, width) => sum + width, 0) || 1;
    if (total <= availableWidth) {
      return baseColumnWidths;
    }
    const scale = availableWidth / total;
    return baseColumnWidths.map((width) => Math.max(minColumnWidth, Math.floor(width * scale)));
  }, [baseColumnWidths, availableWidth]);
  const handleResize = useCallback(
    (targetIndex: number) => (_e: unknown, data: { size: { width: number } }) => {
      const keys = tableColumns.map((col) => String(col.key));
      const current = columnWidths.map((width) => Math.max(minColumnWidth, Math.round(width)));
      const totalAvailable = availableWidth ?? current.reduce((sum, width) => sum + width, 0);
      const rightIndexes = current.map((_width, index) => index).filter((index) => index > targetIndex);
      if (rightIndexes.length === 0) {
        return;
      }
      const rightReducible = rightIndexes.reduce(
        (sum, index) => sum + Math.max(0, current[index] - minColumnWidth),
        0
      );
      const maxTarget = Math.max(minColumnWidth, current[targetIndex] + rightReducible);
      const nextWidth = Math.min(Math.max(Math.round(data.size.width), minColumnWidth), maxTarget);
      const delta = nextWidth - current[targetIndex];
      if (delta === 0) {
        return;
      }

      const updated = [...current];
      updated[targetIndex] = nextWidth;

      if (delta > 0) {
        const reducible = rightIndexes.map((index) => Math.max(0, current[index] - minColumnWidth));
        let remaining = delta;
        const totalReducible = reducible.reduce((sum, value) => sum + value, 0);
        if (totalReducible === 0) {
          return;
        }
        rightIndexes.forEach((index, position) => {
          const room = reducible[position];
          if (room === 0) return;
          const take = Math.min(room, Math.round((delta * room) / totalReducible));
          updated[index] = current[index] - take;
          remaining -= take;
        });
        if (remaining > 0) {
          for (let idx = 0; idx < rightIndexes.length && remaining > 0; idx += 1) {
            const index = rightIndexes[idx];
            if (updated[index] > minColumnWidth) {
              updated[index] -= 1;
              remaining -= 1;
            }
          }
        }
      } else {
        const add = -delta;
        const totalRight = rightIndexes.reduce((sum, index) => sum + current[index], 0);
        let remaining = add;
        rightIndexes.forEach((index) => {
          const give = Math.round((add * current[index]) / (totalRight || 1));
          updated[index] = current[index] + give;
          remaining -= give;
        });
        if (remaining !== 0) {
          for (let idx = 0; idx < rightIndexes.length && remaining !== 0; idx += 1) {
            const index = rightIndexes[idx];
            updated[index] += remaining > 0 ? 1 : -1;
            remaining += remaining > 0 ? -1 : 1;
          }
        }
      }

      const nextWidths: Record<string, number> = {};
      keys.forEach((key, index) => {
        nextWidths[key] = updated[index];
      });
      setColumnWidthByKey((prev) => {
        const merged = { ...prev, ...nextWidths };
        persistColumnWidths(merged);
        return merged;
      });
    },
    [availableWidth, columnWidths, minColumnWidth, persistColumnWidths, tableColumns]
  );

  useEffect(() => {
    const updateAvailableWidth = () => {
      const containerWidth = tableScrollRef.current?.clientWidth ?? 0;
      const borderAdjust = Math.max(totalColumnCount - 1, 0);
      const nextAvailable = Math.max(containerWidth - 48 - borderAdjust, 0);
      setAvailableWidth(nextAvailable || null);
    };

    updateAvailableWidth();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateAvailableWidth);
      return () => window.removeEventListener('resize', updateAvailableWidth);
    }
    const observer = new ResizeObserver(updateAvailableWidth);
    if (tableScrollRef.current) {
      observer.observe(tableScrollRef.current);
    }
    return () => observer.disconnect();
  }, [totalColumnCount]);

  useEffect(() => {
    const container = tableScrollRef.current;
    if (!container) {
      return;
    }
    const updateHeight = () => {
      const rect = container.getBoundingClientRect();
      const nextHeight = Math.max(window.innerHeight - rect.top - 24, 200);
      setViewportHeight(nextHeight);
      const nextScrollbarWidth = Math.max(container.offsetWidth - container.clientWidth, 0);
      setScrollbarWidth(nextScrollbarWidth);
    };
    updateHeight();
    const mutationObserver = new MutationObserver(() => {
      updateHeight();
    });
    mutationObserver.observe(container, { childList: true, subtree: true, attributes: true });
    const handleScroll = () => {
      setScrollTop(container.scrollTop);
    };
    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateHeight);
    return () => {
      mutationObserver.disconnect();
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateHeight);
    };
  }, [filteredData.length]);

  const handleBatchExport = async () => {
    if (!selectAll && selectedRowKeys.length === 0) {
      return;
    }
    try {
      const filters = filter === 'all' ? {} : { type: filter };
      const payload = selectAll
        ? { select_all: true, excluded_ids: excludedRowKeys.map((key) => Number(key)), filters }
        : { ids: selectedRowKeys.map((key) => Number(key)), filters };
      const blob = await businessService.export(payload);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `business-export-${dayjs().format('YYYYMMDD-HHmmss')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error('Failed to export businesses');
    }
  };

  const sortedData = useMemo(() => {
    if (!sortKey || !sortOrder) {
      return filteredData;
    }
    const column = tableColumns.find((col: any) => String(col.key) === sortKey);
    const sorter = column?.sorter;
    if (typeof sorter !== 'function') {
      return filteredData;
    }
    const sorted = [...filteredData].sort(sorter);
    return sortOrder === 'descend' ? sorted.reverse() : sorted;
  }, [filteredData, tableColumns, sortKey, sortOrder]);

  const rowHeight = 44;
  const { focusedRowId, handleTableKeyDown, handleRowClick } = useListingTableFocus<Business>({
    rows: sortedData,
    getRowId: (row) => row.id,
    containerRef: tableScrollRef,
    rowHeight,
  });

  const overscan = 6;
  const totalRows = sortedData.length;
  const visibleRange = useMemo(() => {
    if (!viewportHeight || totalRows === 0) {
      return { start: 0, end: totalRows, topPad: 0, bottomPad: 0 };
    }
    const maxStart = Math.max(0, totalRows - 1);
    const rawStart = Math.min(
      maxStart,
      Math.max(0, Math.floor(scrollTop / rowHeight) - overscan)
    );
    const rawEnd = Math.min(
      totalRows,
      Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan
    );
    const start = Math.min(rawStart, totalRows);
    const end = Math.max(start, rawEnd);
    const topPad = start * rowHeight;
    const bottomPad = Math.max(0, (totalRows - end) * rowHeight);
    return { start, end, topPad, bottomPad };
  }, [overscan, rowHeight, scrollTop, totalRows, viewportHeight]);
  const visibleRows = useMemo(
    () => sortedData.slice(visibleRange.start, visibleRange.end),
    [sortedData, visibleRange]
  );


  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space size={4} wrap>
          <Button
            type={filter === 'all' ? 'primary' : 'default'}
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          <Button
            type={filter === 'potential' ? 'primary' : 'default'}
            onClick={() => setFilter('potential')}
          >
            Potential ({statusCounts.potential})
          </Button>
          <Button
            type={filter === 'active' ? 'primary' : 'default'}
            onClick={() => setFilter('active')}
          >
            Active ({statusCounts.active})
          </Button>
          <Button
            type={filter === 'inactive' ? 'primary' : 'default'}
            onClick={() => setFilter('inactive')}
          >
            Inactive ({statusCounts.inactive})
          </Button>
        </Space>
        <Space>
          <Button
            icon={<ColumnHeightOutlined />}
            onClick={() => {
              setPendingVisibleKeys(visibleKeys);
              setColumnsSearch('');
              setColumnsModalOpen(true);
            }}
          >
            Columns
          </Button>
          {canDelete && (selectAll || selectedRowKeys.length > 0) && (
            <Popconfirm
              title={selectAll ? 'Delete all businesses?' : `Delete ${selectedRowKeys.length} business${selectedRowKeys.length === 1 ? '' : 'es'}?`}
              okText="Delete"
              okButtonProps={{ danger: true, loading: deleting }}
              onConfirm={handleBatchDelete}
            >
              <Button danger loading={deleting}>
                Batch Delete
              </Button>
            </Popconfirm>
          )}
          {(selectAll || selectedRowKeys.length > 0) && (
            <Button
              icon={<span style={{ color: '#9254de' }}><DownloadOutlined /></span>}
              onClick={handleBatchExport}
              style={{
                borderColor: '#9254de',
                color: '#9254de',
              }}
            >
              Batch Export
            </Button>
          )}
          {canCreate && (
            <Button icon={<UploadOutlined />} onClick={handleImportNavigate}>
              Import
            </Button>
          )}
          {canCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/businesses/new')}
            >
              Add Business
            </Button>
          )}
        </Space>
      </div>
      <Modal
        title="Select Columns"
        open={columnsModalOpen}
        onCancel={() => setColumnsModalOpen(false)}
        width={820}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              type="link"
              onClick={() => {
                setPendingVisibleKeys(defaultVisibleKeys);
              }}
            >
              Reset to default
            </Button>
            <Space>
              <Button onClick={() => setColumnsModalOpen(false)}>Cancel</Button>
              <Button
                type="primary"
                onClick={() => {
                  handleColumnVisibilityChangeLimited(pendingVisibleKeys);
                  setColumnsModalOpen(false);
                }}
              >
                Apply
              </Button>
            </Space>
          </div>
        )}
      >
        <Input
          placeholder="Find a column"
          prefix={<SearchOutlined />}
          value={columnsSearch}
          onChange={(event) => setColumnsSearch(event.target.value)}
          style={{ marginBottom: 16 }}
        />
        {columnGroups.map((group) => {
          const groupColumns = group.keys
            .map((key) => columnConfigByKey.get(key))
            .filter((col): col is typeof columnConfig[number] => Boolean(col))
            .filter((col) => filteredColumnConfig.some((filtered) => filtered.key === col.key));

          if (groupColumns.length === 0) {
            return null;
          }

          return (
            <div key={group.title} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
                {group.title.toUpperCase()}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 12,
                }}
              >
                {groupColumns.map((col) => (
                  <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AntCheckbox
                      checked={pendingVisibleKeys.includes(col.key)}
                      disabled={columnSelectionLocked && !pendingVisibleKeys.includes(col.key)}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setPendingVisibleKeys((prev) => [...prev, col.key]);
                        } else {
                          setPendingVisibleKeys((prev) => prev.filter((key) => key !== col.key));
                        }
                      }}
                    />
                    <span style={{ color: columnSelectionLocked && !pendingVisibleKeys.includes(col.key) ? '#9ca3af' : undefined }}>
                      {col.title}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </Modal>
      {submitNotice && (
        <div style={{ marginBottom: 16 }}>
          <Alert color={submitNotice.type} onDismiss={() => setSubmitNotice(null)}>
            {submitNotice.message}
          </Alert>
        </div>
      )}

      <div className="flowbite-listing-table-shell">
        <div style={{ paddingRight: scrollbarWidth }}>
          <FlowbiteTable className="flowbite-listing-table header-only w-full table-fixed" hoverable style={{ width: '100%' }}>
            <colgroup>
              <col style={{ width: 48 }} />
              {tableColumns.map((col, index) => (
                <col key={`head_${String(col.key)}`} style={{ width: columnWidths[index] }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                <TableHeadCell className="w-12">
                  <Checkbox
                    checked={selectAll || (sortedData.length > 0 && selectedRowKeys.length === sortedData.length)}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (e.target.checked) {
                        setSelectAll(true);
                        setExcludedRowKeys([]);
                        setSelectedRowKeys([]);
                      } else {
                        setSelectAll(false);
                        setExcludedRowKeys([]);
                        setSelectedRowKeys([]);
                      }
                    }}
                  />
                </TableHeadCell>
                {tableColumns.map((col, index) => {
                  const key = String(col.key);
                  const isSortable = typeof col.sorter === 'function';
                  const isSorted = sortKey === key;
                  const sortIndicator =
                    isSorted && sortOrder
                      ? sortOrder === 'ascend'
                        ? '▲'
                        : '▼'
                      : '';
                  const isFirstDataColumn = index === 0;
                  const maxWidth =
                    availableWidth != null
                      ? Math.max(minColumnWidth, availableWidth - minColumnWidth * (columnWidths.length - 1))
                      : undefined;

                  return (
                    <FlowbiteResizableHeader
                      key={key}
                      width={columnWidths[index]}
                      maxWidth={maxWidth}
                      onResize={handleResize(index)}
                      onClick={() => handleSort(key, col.sorter)}
                      className={isSortable ? 'cursor-pointer select-none' : undefined}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div>{col.title}</div>
                          {isFirstDataColumn && (
                            <div className="mt-1 text-xs text-gray-400">{totalLabel}</div>
                          )}
                        </div>
                        {isSortable && (
                          <span className="text-xs text-gray-400">{sortIndicator}</span>
                        )}
                      </div>
                    </FlowbiteResizableHeader>
                  );
                })}
              </TableRow>
              <TableRow>
                <TableHeadCell className="w-12" />
                {tableColumns.map((col, index) => {
                  const key = String(col.key);
                  return (
                    <TableHeadCell key={`${key}-search`} className="py-2">
                      {searchRowCells?.[index] ?? null}
                    </TableHeadCell>
                  );
                })}
              </TableRow>
            </TableHead>
          </FlowbiteTable>
        </div>
        <div
          className="flowbite-listing-table-scroll"
          ref={tableScrollRef}
          tabIndex={0}
          onKeyDown={handleTableKeyDown}
          style={
            viewportHeight
              ? {
                  height: viewportHeight,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                }
              : { overflowY: 'auto', overflowX: 'hidden' }
          }
        >
          <FlowbiteTable className="flowbite-listing-table w-full table-fixed" hoverable style={{ width: '100%' }}>
            <colgroup>
              <col style={{ width: 48 }} />
              {tableColumns.map((col, index) => (
                <col key={`body_${String(col.key)}`} style={{ width: columnWidths[index] }} />
              ))}
            </colgroup>
            <TableBody className="divide-y">
              {loading && (
                <TableRow>
                  <TableCell colSpan={tableColumns.length + 1} className="py-8 text-center text-sm text-gray-500">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!loading && sortedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tableColumns.length + 1} className="py-8 text-center text-sm text-gray-500">
                    No businesses
                  </TableCell>
                </TableRow>
              )}
              {!loading && sortedData.length > 0 && (
                <>
                  {visibleRange.topPad > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={tableColumns.length + 1}
                        style={{ height: visibleRange.topPad, padding: 0 }}
                      />
                    </TableRow>
                  )}
                  {visibleRows.map((record: Business, index: number) => {
                    const isSelected = selectedRowKeys.includes(record.id);
                    const isFocused = focusedRowId === record.id;
                    return (
                      <TableRow
                        key={record.id}
                        data-row-id={record.id}
                        className={`hover:bg-gray-50 ${isFocused ? 'bg-blue-50' : ''}`}
                        onDoubleClick={canView ? () => navigate(`/businesses/${record.id}/edit`) : undefined}
                        onClick={() => handleRowClick(record.id)}
                      >
                        <TableCell className="w-12">
                          <Checkbox
                            checked={selectAll ? !excludedRowKeys.includes(record.id) : isSelected}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => {
                              if (selectAll) {
                                setExcludedRowKeys((prev) =>
                                  prev.includes(record.id)
                                    ? prev.filter((key) => key !== record.id)
                                    : [...prev, record.id]
                                );
                                return;
                              }
                              setSelectedRowKeys((prev) =>
                                prev.includes(record.id)
                                  ? prev.filter((key) => key !== record.id)
                                  : [...prev, record.id]
                              );
                            }}
                          />
                        </TableCell>
                        {tableColumns.map((col, index) => {
                          const value = getCellValue(record, col);
                          const content = typeof col.render === 'function'
                            ? col.render(value, record, index)
                            : value ?? '-';
                          return (
                            <TableCell key={`${record.id}_${String(col.key)}`} style={{ width: columnWidths[index] }}>
                              {content}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                  {visibleRange.bottomPad > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={tableColumns.length + 1}
                        style={{ height: visibleRange.bottomPad, padding: 0 }}
                      />
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </FlowbiteTable>
        </div>
      </div>
    </div>
  );
};
